import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import classNames from "classnames";

import { developerPortalUrl, ROUTES } from "../../../setup/constants";
import {
  ACCEPTED_LOGO_TYPES,
  MAX_LOGO_SIZE_BYTES,
} from "../../../setup/api/services/organization";
import { logOut } from "../../../setup/context/authContext";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import {
  canManageApiClients,
  canViewContracts,
  canViewFinanceAndCustomers,
} from "../../../setup/util/permissions";
import styles from "./appSidebar.module.scss";
import {
  BalanceIcon,
  CameraIcon,
  ChevronDownIcon,
  ChevronEndIcon,
  CodeIcon,
  CustomersIcon,
  HomeIcon,
  ImagePlaceholderIcon,
  LogoutIcon,
  ProjectsIcon,
  SettingsIcon,
  TransactionsIcon,
  UpdatesIcon,
} from "./icons";

interface NavItem {
  /** Hidden when false (the user lacks the permission). */
  visible?: boolean;
  key: string;
  label: string;
  Icon: React.FC<{ className?: string }>;
  /** In-app route; omitted for entries that open another site. */
  route?: string;
  /** Another site, opened in a new tab instead of navigating. */
  external?: string;
  active: boolean;
}

interface Props {
  /** Organization name shown in the brand slot. */
  organizationName?: string;
  /** Organization logo; falls back to a placeholder glyph when absent. */
  organizationLogoUrl?: string;
  /** Second line under the organization name — "شركة" in the app, review status in the gate. */
  brandSubLabel?: string;
  /** Account identifier shown beside brandSubLabel (e.g. the organization's phone number). */
  brandIdentifier?: string;
  userName?: string;
  /** Role line under the user's name. */
  userRoleLabel?: string;
  userPhotoUrl?: string;
  /**
   * Renders the nav and the settings/profile menu entries as non-interactive. Used by the
   * organization gate, where every route still resolves to the gate itself, so navigating
   * anywhere is impossible until an admin approves the organization.
   */
  inert?: boolean;
  /**
   * Makes the brand tile a logo picker. Receives the chosen file and should persist it,
   * resolving once saved. Omit to render the tile as display-only.
   */
  onLogoChange?: (file: File) => Promise<void>;
}

/**
 * The portal's single sidebar. Shared by the authenticated app shell and by the
 * organization-gate screens so both render identical chrome — previously the gate had to
 * hand-roll its own copy, which drifted from the app's as soon as either changed.
 */
const AppSidebar: React.FC<Props> = ({
  organizationName,
  organizationLogoUrl,
  brandSubLabel,
  brandIdentifier,
  userName,
  userRoleLabel,
  userPhotoUrl,
  inert = false,
  onLogoChange,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const userWrapRef = useRef<HTMLDivElement>(null);

  const onLogoPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so re-picking the same file after an error still fires onChange.
    e.target.value = "";
    if (!file || !onLogoChange) return;

    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      setLogoError(t("lbl_org_logo_invalid_type"));
      return;
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setLogoError(t("lbl_org_logo_too_large"));
      return;
    }

    setLogoError(null);
    setLogoUploading(true);
    try {
      await onLogoChange(file);
    } catch {
      setLogoError(t("lbl_org_logo_error"));
    } finally {
      setLogoUploading(false);
    }
  };

  const initials = useMemo(() => {
    const parts = (userName ?? "").trim().split(/\s+/).filter(Boolean);
    return parts
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join("");
  }, [userName]);

  // Dismiss the user menu on any click outside it.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocumentClick = (event: MouseEvent) => {
      if (!userWrapRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("click", onDocumentClick);
    return () => document.removeEventListener("click", onDocumentClick);
  }, [menuOpen]);

  const authorities = AuthConfig.getUserInfo()?.authorities;
  const navItems = useMemo<NavItem[]>(
    () => [
      {
        key: "home",
        label: t("lbl_business_home"),
        Icon: HomeIcon,
        route: ROUTES.HOME,
        active: location.pathname === ROUTES.HOME,
      },
      {
        key: "projects",
        label: t("lbl_projects"),
        Icon: ProjectsIcon,
        route: ROUTES.CONTRACTS_LIST,
        active: location.pathname === ROUTES.CONTRACTS_LIST,
        visible: canViewContracts(authorities),
      },
      {
        key: "customers",
        label: t("lbl_customers"),
        Icon: CustomersIcon,
        route: ROUTES.USERS_MANAGEMENT,
        active: location.pathname?.includes(ROUTES.USERS_MANAGEMENT),
        visible: canViewFinanceAndCustomers(authorities),
      },
      {
        key: "updates",
        label: t("lbl_latest_updates"),
        Icon: UpdatesIcon,
        route: ROUTES.LATEST_UPDATES,
        active: location.pathname === ROUTES.LATEST_UPDATES,
        visible: canViewFinanceAndCustomers(authorities),
      },
      {
        key: "transactions",
        label: t("lbl_money_transactions"),
        Icon: TransactionsIcon,
        route: ROUTES.MONEY_TRANSACTIONS,
        active: location.pathname === ROUTES.MONEY_TRANSACTIONS,
        visible: canViewFinanceAndCustomers(authorities),
      },
      {
        key: "balance",
        label: t("lbl_balance"),
        Icon: BalanceIcon,
        route: ROUTES.BALANCE,
        active: location.pathname === ROUTES.BALANCE,
        visible: canViewFinanceAndCustomers(authorities),
      },
      {
        key: "settings",
        label: t("lbl_settings"),
        Icon: SettingsIcon,
        route: ROUTES.SETTINGS,
        active: location.pathname === ROUTES.SETTINGS,
      },
      // Opens the developer portal (docs, Postman collection) in a new tab; hidden when the URL is
      // not configured for this environment.
      ...(developerPortalUrl("/dashboard")
        ? [
            {
              key: "developer",
              label: t("lbl_developer_portal"),
              Icon: CodeIcon,
              external: developerPortalUrl("/dashboard"),
              active: false,
              visible: canManageApiClients(authorities),
            },
          ]
        : []),
    ],
    [location.pathname, t, authorities]
  );
  const visibleNavItems = navItems.filter((item) => item.visible !== false);

  return (
    <aside
      className={classNames(styles.sidebar, {
        [styles.isCollapsed]: collapsed,
        [styles.isMenuOpen]: menuOpen,
        [styles.isInert]: inert,
      })}>
      <button
        className={styles.collapseToggle}
        type="button"
        aria-label={t("lbl_toggle_menu") as string}
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((prev) => !prev)}>
        <ChevronEndIcon />
      </button>

      <div className={styles.brand}>
        {(() => {
          const tile = (
            <span
              className={classNames(styles.brandLogo, {
                [styles.hasLogo]: !!organizationLogoUrl,
              })}
              style={
                organizationLogoUrl
                  ? { backgroundImage: `url(${organizationLogoUrl})` }
                  : undefined
              }>
              {logoUploading ? (
                <span className={styles.logoSpinner} />
              ) : (
                <>
                  {!organizationLogoUrl && <ImagePlaceholderIcon />}
                  {/* Hover affordance, only when the tile is actually editable. */}
                  {onLogoChange && (
                    <span className={styles.logoEditOverlay}>
                      <CameraIcon />
                    </span>
                  )}
                </>
              )}
            </span>
          );

          const text = (
            <span className={styles.brandText}>
              {/* dir="auto" so a Latin name ellipsizes at its own end ("Advanced Busi…")
                  instead of losing its first word to the RTL block's overflow edge. */}
              <b dir="auto">{organizationName}</b>
              {logoError ? (
                <span className={styles.brandError}>{logoError}</span>
              ) : (
                (brandSubLabel || brandIdentifier) && (
                  <span>
                    {brandSubLabel}
                    {brandSubLabel && brandIdentifier && " · "}
                    {brandIdentifier && <bdi>{brandIdentifier}</bdi>}
                  </span>
                )
              )}
            </span>
          );

          // A label (not a button) so the hidden file input is driven natively, and so the
          // whole brand row is a click target — matching the design's logo-upload block.
          return onLogoChange ? (
            <label
              className={classNames(styles.brandInner, styles.isEditable, {
                [styles.isBusy]: logoUploading,
              })}
              title={t("lbl_org_logo_change") as string}>
              <input
                type="file"
                accept={ACCEPTED_LOGO_TYPES.join(",")}
                hidden
                disabled={logoUploading}
                onChange={onLogoPicked}
              />
              {tile}
              {text}
            </label>
          ) : (
            <div className={styles.brandInner}>
              {tile}
              {text}
            </div>
          );
        })()}
      </div>

      <nav className={styles.nav}>
        {visibleNavItems.map(({ key, label, Icon, route, external, active }) =>
          inert ? (
            <div
              key={key}
              aria-disabled="true"
              className={classNames(styles.navItem, {
                // Nothing is navigable, so "home" stands in as the resting state.
                [styles.isActive]: key === "home",
              })}>
              <Icon />
              {label}
            </div>
          ) : (
            <button
              key={key}
              type="button"
              className={classNames(styles.navItem, {
                [styles.isActive]: active,
              })}
              onClick={() =>
                external
                  ? window.open(external, "_blank", "noopener,noreferrer")
                  : navigate(route as string)
              }>
              <Icon />
              {label}
            </button>
          )
        )}
      </nav>

      <div ref={userWrapRef} className={styles.userWrap}>
        <div className={styles.userMenu}>
          <button
            type="button"
            disabled={inert}
            aria-disabled={inert}
            onClick={() => {
              setMenuOpen(false);
              navigate(ROUTES.SETTINGS);
            }}>
            <SettingsIcon />
            {t("lbl_settings")}
          </button>
          {/* Profile entry temporarily hidden — restore when the profile page ships. */}
          {/* <button
            type="button"
            disabled={inert}
            aria-disabled={inert}
            onClick={() => {
              setMenuOpen(false);
              navigate(ROUTES.PROFILE);
            }}>
            <ProfileIcon />
            {t("lbl_profile")}
          </button> */}
          <button
            className={styles.isDanger}
            type="button"
            // OIDC front-channel single logout: logOut() redirects to Keycloak's end-session
            // endpoint and back. No manual reload — that would race the redirect.
            onClick={() => logOut()}>
            <LogoutIcon />
            {t("lbl_logout")}
          </button>
        </div>
        <button
          className={styles.userTile}
          type="button"
          aria-haspopup="true"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}>
          {userPhotoUrl ? (
            <img className={styles.avatar} src={userPhotoUrl} alt="" />
          ) : (
            <span className={styles.avatar}>{initials}</span>
          )}
          <span className={styles.userText}>
            <b dir="auto">{userName}</b>
            {userRoleLabel && <span>{userRoleLabel}</span>}
          </span>
          <ChevronDownIcon className={styles.chev} />
        </button>
      </div>
    </aside>
  );
};

export default AppSidebar;
